import React from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import { getDependents, getEmployeeApplication, getEmployeeForm, useUpdateEmployeePolicyMutation } from "EmployeeApplication/api";
import { getEnrollmentSegment } from "EmployeeApplication/selectors";
import AgeEnrollmentModal, { AgeEnrollmentFormValueProps } from "EmployeeCoverage/Components/EnrollmentModal/AgeEnrollmentModal";
import SegmentEnrollmentModal from "EmployeeCoverage/Components/EnrollmentModal/SegmentEnrollmentModal";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { formatNameFL } from "Utils/formatName";
import { EmployeeForm, EmployeePolicy, LifePlan, PlanCost } from "Utils/types";

import Button, { ButtonProps } from "Cobalt/Button";

import { useLifeContext } from "./life.context";

interface EntitiesToAgeFormInitialValuesProps {
  employeeForm: EmployeeForm;
  employeePolicy: EmployeePolicy;
}
const entitiesToAgeFormInitialValues = ({ employeeForm, employeePolicy }: EntitiesToAgeFormInitialValuesProps): AgeEnrollmentFormValueProps => {
  return {
    employeeEnrolling: employeePolicy.isEnrollingLife,
    employeeFormattedName: formatNameFL(employeeForm),
    dependents: [],
  };
};

export const LifeEnrollmentModals = () => {
  const { plan, planCost, isShowing, hideEnrollmentModal } = useLifeContext();
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const employeeDependents = useSelector(getDependents) || [];
  const dispatch = useDispatch();

  const closeModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "close_enroll_or_decline_modal",
    });
    hideEnrollmentModal();
  };

  const onSubmitAgeEnrollment = async (values) => {
    const isEnrollingLife = values.employeeEnrolling;
    const body = {
      id: employeePolicy.id,
      isEnrollingLife: values.employeeEnrolling,
      enrolledLifePlanID: isEnrollingLife === "Yes" ? plan.id : null,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseLifePlan"));
    }
  };

  if (planCost?.pricingType === "age") {
    return (
      <AgeEnrollmentModal
        plan={plan}
        employeePolicyID={employeePolicy.id}
        initialValues={entitiesToAgeFormInitialValues({
          employeeForm,
          employeePolicy,
        })}
        title="Enroll In Life Coverage"
        onSubmit={onSubmitAgeEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  const currentSegment = getEnrollmentSegment(employeePolicy, employeeDependents, "life");

  const onSubmitSegmentEnrollment = async ({ selectedSegment }) => {
    const body = {
      id: employeePolicy.id,
      isEnrollingLife: selectedSegment === "none" ? "No" : "Yes",
      enrolledLifePlanID: selectedSegment === "none" ? null : plan.id,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseLifePlan"));
    }
  };

  if (planCost?.pricingType === "segment") {
    return (
      <SegmentEnrollmentModal
        plan={plan}
        planCost={planCost}
        title="Enroll In Life Coverage"
        initialValues={{ selectedSegment: currentSegment }}
        onSubmit={onSubmitSegmentEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  return null;
};

interface LifeEnrollOrDeclineButtonProps extends ButtonProps {
  plan: LifePlan;
  planCost: PlanCost;
}

export const LifeEnrollOrDeclineButton = ({ plan, planCost, ...rest }: LifeEnrollOrDeclineButtonProps) => {
  const { openEnrollmentModal } = useLifeContext();
  const openModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "open_enroll_or_decline_modal",
    });
    openEnrollmentModal({ plan, planCost });
  };

  return (
    <Button onClick={openModal} {...rest}>
      Enroll or Decline
    </Button>
  );
};

export const LifeIneligibleButton = ({ plan, planCost, ...rest }: LifeEnrollOrDeclineButtonProps) => {
  const dispatch = useDispatch();
  const employeePolicy = useSelector(getEmployeeApplication);
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();

  const declineCoverage = async () => {
    const body = {
      id: employeePolicy.id,
      isEnrollingLife: "No",
      enrolledLifePlanID: null,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseLifePlan"));
    }
  };

  return (
    <Button onClick={declineCoverage} {...rest}>
      Continue
    </Button>
  );
};
